import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Save } from 'lucide-react';
import './CreateMovementModal.css';

function CreateMovementModal({ isOpen, onClose, onSave, items = [] }) {
  const { t } = useTranslation();

  const MOVEMENT_TYPES = [
    { value: 'STOCK_IN', label: t('inventory.movementsPage.typeStockInLabel'), color: '#10b981' },
    { value: 'STOCK_OUT', label: t('inventory.movementsPage.typeStockOutLabel'), color: '#ef4444' },
    {
      value: 'ADJUSTMENT',
      label: t('inventory.movementsPage.typeAdjustmentLabel'),
      color: '#f59e0b',
    },
  ];

  const [formData, setFormData] = useState({
    itemId: '',
    type: 'STOCK_IN',
    quantity: '',
    reason: '',
    cost: '',
    expirationDate: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!formData.itemId || !formData.quantity) {
        throw new Error(t('inventory.movementsPage.requiredFieldsError'));
      }

      await onSave({
        ...formData,
        quantity: parseFloat(formData.quantity),
        cost: formData.cost ? parseFloat(formData.cost) : undefined,
        expirationDate: formData.expirationDate || undefined,
      });
      onClose();
      // Reset form
      setFormData({
        itemId: '',
        type: 'STOCK_IN',
        quantity: '',
        reason: '',
        cost: '',
        expirationDate: '',
      });
    } catch (err) {
      const serverMessage = err.response?.data?.message || err.response?.data?.error;
      if (serverMessage) {
        setError(serverMessage);
      } else if (err.response?.status === 400) {
        setError(
          t(
            'inventory.movementsPage.invalidRequest',
            'Datos inválidos. Verifique cantidad y stock disponible.'
          )
        );
      } else {
        setError(err.message || t('inventory.movementsPage.saveError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedItem = items.find((i) => i.itemId === formData.itemId);

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{t('inventory.movementsPage.registerTitle')}</h2>
          <button type="button" onClick={onClose} className="close-button">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="movement-form">
          {error && (
            <div className="error-message" role="alert" aria-live="polite">
              {error}
            </div>
          )}

          <div className="form-group">
            <label>{t('inventory.movementsPage.productRequired')}</label>
            <select
              name="itemId"
              value={formData.itemId}
              onChange={handleChange}
              required
              className="form-input"
            >
              <option value="">{t('inventory.movementsPage.selectProduct')}</option>
              {items.map((item) => (
                <option key={item.itemId} value={item.itemId}>
                  {item.name} ({t('inventory.movementsPage.currentStock')}: {item.stock} {item.unit}
                  )
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>{t('inventory.movementsPage.movementType')}</label>
            <div
              className="type-selector"
              role="radiogroup"
              aria-label={t('inventory.movementsPage.movementType')}
            >
              {MOVEMENT_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  role="radio"
                  aria-checked={formData.type === type.value}
                  className={`type-button ${formData.type === type.value ? 'selected' : ''}`}
                  onClick={() => setFormData((prev) => ({ ...prev, type: type.value }))}
                  style={{
                    backgroundColor: formData.type === type.value ? type.color : 'transparent',
                    borderColor: type.color,
                    color: formData.type === type.value ? 'white' : type.color,
                  }}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                {formData.type === 'ADJUSTMENT'
                  ? t('inventory.movementsPage.realStock')
                  : t('inventory.movementsPage.quantity')}{' '}
                *
              </label>
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                required
                className="form-input"
              />
              {selectedItem && <span className="unit-label">{selectedItem.unit}</span>}
            </div>

            {formData.type === 'STOCK_IN' && (
              <div className="form-group">
                <label>{t('inventory.movementsPage.expirationOptional')}</label>
                <input
                  type="date"
                  name="expirationDate"
                  value={formData.expirationDate || ''}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            )}
          </div>

          {formData.type === 'STOCK_IN' && (
            <div className="form-group">
              <label>{t('inventory.movementsPage.totalCostOptional')}</label>
              <input
                type="number"
                name="cost"
                value={formData.cost}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                className="form-input"
              />
            </div>
          )}

          {formData.type === 'ADJUSTMENT' && selectedItem && formData.quantity && (
            <div className="adjustment-preview">
              <p>
                {t('inventory.movementsPage.difference')}:{' '}
                <strong>
                  {(parseFloat(formData.quantity) - selectedItem.stock).toFixed(2)}{' '}
                  {selectedItem.unit}
                </strong>
              </p>
            </div>
          )}

          <div className="form-group">
            <label>{t('inventory.movementsPage.reasonLabel')}</label>
            <textarea
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              placeholder={t('inventory.movementsPage.reasonPlaceholder')}
              rows={3}
              className="form-input"
            />
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-ghost">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? (
                t('inventory.movementsPage.saving')
              ) : (
                <>
                  <Save size={18} />
                  {t('inventory.movementsPage.registerMovement')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateMovementModal;
